/**
 * CTA/Play Shortcode Parser
 * Replaces [cta] and [play] shortcodes in text nodes.
 *
 * Important:
 * - Play buttons are rendered from DOM templates provided by `snippets/shortcode-templates.liquid`.
 * - Expected template IDs:
 *   - shortcode-template-play-audio
 *   - shortcode-template-play-video
 *
 * Usage:
 * [cta ...]
 * [cta label="My button" link="#my-link"]
 * [cta type="primary" label="Primary button" link="/collections/all"]
 * [cta type="secondary" label="Secondary button" link="/pages/about"]
 * [cta label="External link" link="https://example.com" target="_blank" title="Opens in new tab"]
 * [cta label="With extra class" link="/products/demo" className="my-custom-class another-class"]
 *
 * [play ...]
 * [play type="audio" id="9332116553953" label="River Rerun"]
 * [play type="video" url="https://www.youtube.com/watch?v=jWZCB5MFYT4" label="River Rerun"]
 * [play type="video" url="https://youtu.be/jWZCB5MFYT4" title="Watch River Rerun"]
 *
 * CTA attributes:
 * - label (required)
 * - link (required)
 * - type (optional: primary | secondary)
 * - target (optional: e.g. _blank)
 * - title (optional)
 * - className (optional: extra classes appended to base button class)
 *
 * Play attributes:
 * - type (required: audio | video)
 * - id (required for audio, rendered as #play-<id>)
 * - url (required for video)
 * - label (optional)
 * - title (optional)
 * - className (optional: extra classes appended to base media button class)
 */
class ButtonShortcode {
  constructor() {
    /**
     * Selectors, where the replacement are fired.
     */
    this.selectors = [
      ".rte",
      "rte-formatter",
      ".text-block.custom",
      ".custom-text-table__table",
    ];

    /**
     * IDs provided by `snippets/shortcode-templates.liquid`
     */
    this.templateIds = {
      playAudio: "shortcode-template-play-audio",
      playVideo: "shortcode-template-play-video",
    };

    /**
     * Wrapper class while waiting for parse (must match Liquid + base.css). Stripped in parseAll finally.
     */
    this.pendingClassName = "shortcodes-pending";

    /**
     * Config for the button shortcodes
     */
    this.shortcodes = [
      {
        name: "cta",
        attributes: ["type", "label", "link", "target", "title", "className"],
        renderFn: (/** @type {string} */ attrString) =>
          this.renderCtaShortcode(attrString),
      },
      {
        name: "play",
        attributes: ["type", "label", "id", "url", "title", "className"],
        renderFn: (/** @type {string} */ attrString) =>
          this.renderPlayShortcode(attrString),
      },
    ];
    this.init();
  }

  init() {
    this.parseAll();

    // Theme editor / dynamic section rendering
    document.addEventListener("shopify:section:load", () => this.parseAll());
    document.addEventListener("shopify:section:reorder", () => this.parseAll());
  }

  parseAll() {
    try {
      const containers = this.getContainers();
      if (containers.length === 0) return;

      const hasAnyShortcode = containers.some((container) =>
        this.hasAnyShortcode(container.textContent || ""),
      );
      if (!hasAnyShortcode) return;

      let total = 0;

      for (const container of containers) {
        for (const { name, renderFn } of this.shortcodes) {
          total += this.replaceShortcodesInDom(container, name, renderFn);
        }
      }

      if (total > 0) {
        // eslint-disable-next-line no-console
        console.log(`ButtonShortcode: Replaced ${total} shortcode(s)`);
      }
    } finally {
      const pendingSelector = `.${this.pendingClassName}`;
      document.querySelectorAll(pendingSelector).forEach((el) => {
        el.classList.remove(this.pendingClassName);
      });
    }
  }

  getContainers() {
    const set = new Set();

    for (const selector of this.selectors) {
      for (const el of document.querySelectorAll(selector)) {
        set.add(el);
      }
    }

    return Array.from(set);
  }

  /**
   * Replace shortcodes of a given name in text nodes only.
   *
   * @param {Element} containerEl
   * @param {string} name
   * @param {(attrString: string) => Element | null} renderFn
   */
  replaceShortcodesInDom(containerEl, name, renderFn) {
    const regex = this.createShortcodeRegex(name);

    const walker = document.createTreeWalker(
      containerEl,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentNode;
          if (!parent || parent.nodeType !== Node.ELEMENT_NODE)
            return NodeFilter.FILTER_REJECT;

          const tagName = /** @type {Element} */ (parent).tagName;
          if (
            tagName === "SCRIPT" ||
            tagName === "STYLE" ||
            tagName === "TEXTAREA"
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          const value = node.nodeValue || "";
          return this.hasAnyShortcode(value)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      },
    );

    let replacements = 0;
    const nodesToProcess = [];
    while (walker.nextNode()) nodesToProcess.push(walker.currentNode);

    for (const node of nodesToProcess) {
      const text = node.nodeValue || "";
      const result = this.replaceInText(text, regex, renderFn);
      if (!result) continue;

      node.parentNode?.replaceChild(result.fragment, node);
      replacements += result.count;
    }

    return replacements;
  }

  /**
   * @param {string} text
   */
  hasAnyShortcode(text) {
    return this.shortcodes.some(({ name }) => text.includes(`[${name}`));
  }

  /**
   * @param {string} name
   */
  createShortcodeRegex(name) {
    return new RegExp(`\\[${name}\\s+([^\\]]+)\\]`, "gi");
  }

  /**
   * @param {string} text
   * @param {RegExp} regex
   * @param {(attrString: string) => Element | null} renderFn
   */
  replaceInText(text, regex, renderFn) {
    regex.lastIndex = 0;
    let cursor = 0;
    let count = 0;
    const fragment = document.createDocumentFragment();

    let match;
    while ((match = regex.exec(text)) !== null) {
      const before = text.slice(cursor, match.index);
      if (before) fragment.appendChild(document.createTextNode(before));

      const rendered = renderFn(match[1] || "");
      if (rendered) {
        fragment.appendChild(rendered);
        count += 1;
      } else {
        fragment.appendChild(document.createTextNode(match[0]));
      }

      cursor = match.index + match[0].length;
    }

    if (count === 0) return null;

    const after = text.slice(cursor);
    if (after) fragment.appendChild(document.createTextNode(after));

    return { fragment, count };
  }

  /**
   * @param {string} attrString
   * @param {string} attribute
   */
  readAttribute(attrString, attribute) {
    const escapedAttribute = attribute.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Match key="value" and key='value' using the same opening/closing quote.
    // This keeps apostrophes inside double-quoted values intact (e.g. "What's included").
    const regex = new RegExp(
      `${escapedAttribute}\\s*=\\s*(["'])([\\s\\S]*?)\\1`,
      "i",
    );
    const match = attrString.match(regex);
    // match[1] is the quote char (" or '), match[2] is the captured value.
    return match ? match[2] : undefined;
  }

  /**
   * Parse an attrString into an object using the shortcode config.
   *
   * @param {string} attrString
   * @param {string} shortcodeName
   */
  parseAttributes(attrString, shortcodeName) {
    const config = this.shortcodes.find(({ name }) => name === shortcodeName);
    if (!config) return {};

    /** @type {Record<string, string | undefined>} */
    const attrs = {};

    for (const attrName of config.attributes) {
      attrs[attrName] = this.readAttribute(attrString, attrName);
    }

    return attrs;
  }

  /**
   * @param {string} attrString
   */
  renderCtaShortcode(attrString) {
    const attrs = this.parseAttributes(attrString, "cta");
    if (!attrs.label || !attrs.link) return null;

    return this.renderButtonEl({
      label: attrs.label,
      link: attrs.link,
      type: attrs.type,
      target: attrs.target,
      title: attrs.title,
      className: attrs.className,
    });
  }

  /**
   * @param {string} attrString
   */
  renderPlayShortcode(attrString) {
    const attrs = this.parseAttributes(attrString, "play");
    return this.renderPlayEl(attrs);
  }

  /**
   * @param {unknown} rawHref
   */
  sanitizeHref(rawHref) {
    const href = String(rawHref || "").trim();
    if (href === "") return "#";

    const isSafe =
      href.startsWith("#") ||
      href.startsWith("/") ||
      href.startsWith("https://") ||
      href.startsWith("http://") ||
      href.startsWith("mailto:") ||
      href.startsWith("tel:");

    return isSafe ? href : "#";
  }

  /**
   * @param {unknown} rawUrl
   */
  sanitizeVideoUrl(rawUrl) {
    const value = String(rawUrl || "").trim();
    if (value === "") return "";

    try {
      const parsed = new URL(value, window.location.origin);
      return parsed.protocol === "https:" || parsed.protocol === "http:"
        ? parsed.href
        : "";
    } catch {
      return "";
    }
  }

  /**
   * @param {unknown} rawId
   */
  sanitizePlayId(rawId) {
    const id = String(rawId || "").trim();
    if (id === "") return "";

    const normalized = id.replace(/^#play-/, "");
    return /^[a-zA-Z0-9_-]+$/.test(normalized) ? normalized : "";
  }

  /**
   * @param {unknown} rawClassName
   */
  sanitizeClassName(rawClassName) {
    const value = String(rawClassName || "").trim();
    if (value === "") return "";

    return value
      .split(/\s+/)
      .filter((className) => /^[a-zA-Z0-9_-]+$/.test(className))
      .join(" ");
  }

  /**
   * @param {{ type?: string; title?: string; label: string; link: string; target?: string; className?: string }} attrs
   */
  renderButtonEl(attrs) {
    const rawType =
      attrs.type == null ? "" : String(attrs.type).trim().toLowerCase();
    const hasExplicitType = rawType !== "";
    const type = rawType === "secondary" ? "secondary" : "primary";

    let className = "button button-shortcode";
    if (hasExplicitType) className = `${className} button-${type}`;
    const customClassName = this.sanitizeClassName(attrs.className);
    if (customClassName) className = `${className} ${customClassName}`;

    const href = this.sanitizeHref(attrs.link);
    const label = String(attrs.label);
    const target = attrs.target ? String(attrs.target).trim() : "";

    const a = document.createElement("a");
    a.href = href;
    a.className = className;
    a.textContent = label;

    if (attrs.title) a.title = String(attrs.title);
    if (target) a.target = target;
    if (target === "_blank") a.rel = "noopener noreferrer";

    return a;
  }

  /**
   * @param {{ type?: string; title?: string; label?: string; id?: string; url?: string; className?: string }} attrs
   */
  renderPlayEl(attrs) {
    const playType = String(attrs.type || "")
      .trim()
      .toLowerCase();
    if (playType !== "audio" && playType !== "video") return null;

    if (playType === "audio") {
      const playId = this.sanitizePlayId(attrs.id);
      if (!playId) return null;

      return this.renderPlayAudioFromTemplate({
        playId,
        label: attrs.label,
        title: attrs.title,
        className: attrs.className,
      });
    }

    const videoUrl = this.sanitizeVideoUrl(attrs.url);
    if (!videoUrl) return null;

    return this.renderPlayVideoFromTemplate({
      videoUrl,
      label: attrs.label,
      title: attrs.title,
      className: attrs.className,
    });
  }

  /**
   * Clone the global `<template>` for the audio play button.
   * @param {{ playId: string; label?: string; title?: string; className?: string }} params
   */
  renderPlayAudioFromTemplate(params) {
    const template = document.getElementById(this.templateIds.playAudio);
    if (!template) return null;
    if (!(template instanceof HTMLTemplateElement)) return null;

    const fragment = /** @type {DocumentFragment} */ (
      template.content.cloneNode(true)
    );
    const el = /** @type {Element | null} */ (fragment.firstElementChild);
    if (!el) return null;

    // href: "#play-%%AUDIO_ID%%" -> "#play-<id>"
    const href = el.getAttribute("href");
    if (href)
      el.setAttribute("href", href.replace("%%AUDIO_ID%%", params.playId));

    this.updateMediaButton(el, {
      label: params.label,
      className: params.className,
      title: params.title,
    });

    return el;
  }

  /**
   * Clone the global `<template>` for the video play button.
   *
   * @param {{ videoUrl: string; label?: string; title?: string; className?: string }} params
   */
  renderPlayVideoFromTemplate(params) {
    const template = document.getElementById(this.templateIds.playVideo);
    if (!template) return null;
    if (!(template instanceof HTMLTemplateElement)) return null;

    const fragment = /** @type {DocumentFragment} */ (
      template.content.cloneNode(true)
    );
    const el = /** @type {Element | null} */ (fragment.firstElementChild);
    if (!el) return null;

    // data-video-url="%%VIDEO_URL%%" -> data-video-url="<url>"
    const dataVideoUrl = el.getAttribute("data-video-url");
    if (dataVideoUrl)
      el.setAttribute(
        "data-video-url",
        dataVideoUrl.replace("%%VIDEO_URL%%", params.videoUrl),
      );

    this.updateMediaButton(el, {
      label: params.label,
      className: params.className,
      title: params.title,
    });

    return el;
  }

  /**
   * Update a reply media button cloned from a template.
   * Keeps the same behavior as the Liquid snippets regarding label-dependent classes.
   *
   * @param {Element} buttonEl
   * @param {{ label?: unknown; className?: unknown; title?: unknown }} params
   */
  updateMediaButton(buttonEl, params) {
    this.applyMediaButtonLabel(buttonEl, params.label);
    buttonEl.classList.add("button-shortcode");

    const customClassName = this.sanitizeClassName(params.className);
    if (customClassName)
      buttonEl.className = `${buttonEl.className} ${customClassName}`.trim();

    if (params.title) buttonEl.setAttribute("title", String(params.title));
  }

  /**
   * @param {Element} buttonEl
   * @param {unknown} label
   */
  applyMediaButtonLabel(buttonEl, label) {
    const labelValue = String(label || "").trim();
    const labelEl = buttonEl.querySelector(".reply-media-action-btn__label");

    if (labelValue) {
      if (labelEl) labelEl.textContent = labelValue;
      buttonEl.classList.add("button-unstyled--with-icon");
      buttonEl.classList.remove("button-unstyled");
      return;
    }

    if (labelEl) labelEl.remove();
    buttonEl.classList.add("button-unstyled");
    buttonEl.classList.remove("button-unstyled--with-icon");
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new ButtonShortcode();
});

export { ButtonShortcode };
